import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';
import {
    getPageSelector,
    getTitleSelector,
    getPageURLSelector,
    getBreadcrumSelector,
    getLanguageSelector,
    getDatePickerOptionsSelector,
    getNotificationSelector,
    getHelpSelector,
    getUserTypeListSelector,
    getCommissionsSelector
  } from '@store/page/page.selectors';
import {
    changePageInfo,
    changeDateOption,
    resetOption,
    notificationToggle,
    getHelp,
    fetchUserTypes,
    getCommissions
  } from '@store/page/page.actions';

import { Store } from '@ngrx/store';
import { PageState, NotificationState } from '@models/index';
import { IDisableDateOption } from '@interfaces/index';

@Injectable({
  providedIn: 'root',
})

export class PageService {
  backendUrl = environment.backendUrl;

  constructor(
    private http: HttpClient,
    private store: Store<{page: PageState}>,
  ) {
    this.changePageInfo({ language: localStorage.getItem('language') || environment.language });
  }

  public getPageInfoSelector(): Observable<PageState> {
    return this.store.select(getPageSelector);
  }

  public getPageTitleSelector(): Observable<string> {
    return this.store.select(getTitleSelector);
  }

  public getPageBreadcrumSelector(): Observable<string[]> {
    return this.store.select(getBreadcrumSelector);
  }

  public getPageURLSelector(): Observable<string> {
    return this.store.select(getPageURLSelector);
  }

  public getDatePickerOptionsSelector(): Observable<string> {
    return this.store.select(getDatePickerOptionsSelector);
  }

  public getNotificationSelector(): Observable<NotificationState> {
    return this.store.select(getNotificationSelector);
  }

  public getLanguageSelector(): Observable<string> {
    return this.store.select(getLanguageSelector);
  }

  public getHelpSelector(): Observable<any> {
    return this.store.select(getHelpSelector);
  }

  public getUserTypeListSelector(): Observable<any[]> {
    return this.store.select(getUserTypeListSelector);
  }

  public fetchUserTypes(): void {
    this.store.dispatch(fetchUserTypes());
  }

  public getUserTypes() {
    return this.http.get(`${this.backendUrl}/users/user-types`);
  }

  public dispatchHelp(key: string): void {
    this.store.dispatch(getHelp({ key }));
  }

  public getHelp(key: string): Observable<any> {
    return this.http.get(`${this.backendUrl}/helps/help/${key}`);
  }

  public onNotificationToggle(option: NotificationState): void {
    this.store.dispatch(notificationToggle({ option }));
  }

  public changePageInfo(page): void {
    this.store.dispatch(changePageInfo({ page }));
  }

  public changeDateOption(option: any) {
    this.store.dispatch(changeDateOption({ option }));
  }

  public resetOption(optionName: string) {
    this.store.dispatch(resetOption({ optionName }));
  }

  public disableDate(options: IDisableDateOption) {
    this.resetOption('disableUntil');
    this.resetOption('disableSince');

    if (options.date) {
      const disableOption = options.name.includes('from') ? 'disableSince' : 'disableUntil';
      const dateOptions: Date = new Date(options.date);
      this.changeDateOption({
        [disableOption]: {
            year: dateOptions.getFullYear(),
            month: dateOptions.getMonth() + 1,
            day: dateOptions.getDate()
          }
      });
    };
  }

  public getCommissions(params: any = {}) {
    return this.http.get(environment.backendUrl + '/reports/commissions', {params});
  }

  public dispatchCommissions(params = {}) {
    return this.store.dispatch(getCommissions({payload: params}));
  }

  public getCommissionsSelector(): Observable<any> {
    return this.store.select(getCommissionsSelector);
  }
}
